import React, { useState } from 'react';
import { FaUser } from '@react-icons/all-files/fa/FaUser';

// Full-host URL so it works inside datagrids / iframes regardless of dev-server proxy
const AVATAR_BASE = 'http://localhost:9090/public/usuario';

// ── Props ─────────────────────────────────────────────────────────────────────

export interface UserInfoProps {
  /** Full usuario object. When null/undefined renders an em dash. */
  usuario?: any | null;
  /** Called when the user clicks the name. Pass e.g. a filter callback.
   *  When provided the name renders as an indigo underlined link. */
  onClick?: (usuario: any) => void;
  /** Avatar diameter in px (default 26) */
  size?: number;
  /** Hide the text name, show only the avatar (default false) */
  avatarOnly?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function UserInfo({ usuario, onClick, size = 26, avatarOnly = false }: UserInfoProps) {
  if (!usuario?.id) {
    return <span style={s.empty}>—</span>;
  }

  const fullName = `${usuario.nombre ?? ''} ${usuario.apellido ?? ''}`.trim();
  const isLink = !!onClick;

  const handleClick = (e: React.MouseEvent) => {
    if (!onClick) return;
    e.stopPropagation();
    onClick(usuario);
  };

  return (
    <div
      style={{
        ...s.root,
        cursor: isLink ? 'pointer' : 'default',
      }}
      onClick={isLink ? handleClick : undefined}
      title={fullName}
    >
      <UserAvatar usuario={usuario} size={size} />

      {!avatarOnly && (
        <span style={isLink ? s.nameLink : s.name}>
          {fullName || `#${usuario.id}`}
        </span>
      )}
    </div>
  );
}

// ── Avatar with fallback ───────────────────────────────────────────────────────

export function UserAvatar({ usuario, size = 26 }: { usuario: any; size?: number }) {
  const [imgError, setImgError] = useState(false);

  const initials = `${(usuario.nombre ?? '')[0] ?? ''}${(usuario.apellido ?? '')[0] ?? ''}`.toUpperCase();
  const src = `${AVATAR_BASE}/${usuario.id}/image.png`;

  if (imgError) {
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        background: '#e0e7ff', color: '#6366f1',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: Math.round(size * 0.38), fontWeight: 700,
        border: '1px solid #c7d2fe',
      }}>
        {initials || <FaUser size={size * 0.45} />}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={initials}
      style={{
        width: size, height: size, borderRadius: '50%',
        objectFit: 'cover', flexShrink: 0,
        border: '1px solid #e2e8f0',
      }}
      onError={() => setImgError(true)}
    />
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    maxWidth: '100%',
    overflow: 'hidden',
  },
  name: {
    fontSize: 12,
    fontWeight: 600,
    color: '#1e293b',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  nameLink: {
    fontSize: 12,
    fontWeight: 600,
    color: '#6366f1',
    textDecoration: 'underline',
    textUnderlineOffset: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  empty: {
    fontSize: 12,
    color: '#94a3b8',
  },
};
